import KaboodleToaster from "components/common/KaboodleToaster";
import { useCallback, useState } from "react";
import {
  useCreateSubmissionMutation,
  useSubmitIntakeMutation,
} from "services/intake-api";
import { useIntakeToken, useToken } from "utils/context";
import { extractErrorMessage } from "utils/error-utils";
import { logToSentry } from "utils/sentry";

import type { FormData } from "../shared/IntakeTypes";
import { useOrganizationSlug } from "./useOrganizationSlug";

import type { IntakeSubmissionRequest } from "services/intake-api";

type SubmissionState = {
  isSubmitting: boolean;
  progress: number;
  message: string;
  isComplete: boolean;
  showSuccessModal: boolean;
  submissionIdentifier: string | null;
  isInitializing: boolean;
};

export const useIntakeSubmission = () => {
  const { accessToken } = useToken();
  const { intakeToken } = useIntakeToken();
  const organizationSlug = useOrganizationSlug();
  const [submitIntake] = useSubmitIntakeMutation();
  const [createSubmission] = useCreateSubmissionMutation();

  const [submissionState, setSubmissionState] = useState<SubmissionState>({
    isSubmitting: false,
    progress: 0,
    message: "Preparing your submission...",
    isComplete: false,
    showSuccessModal: false,
    submissionIdentifier: null,
    isInitializing: false,
  });

  // lazy initialize submission when first document is uploaded
  const initializeSubmission = useCallback(async () => {
    // don't initialize if already initialized or currently initializing
    if (
      submissionState.submissionIdentifier ||
      submissionState.isInitializing
    ) {
      return submissionState.submissionIdentifier;
    }

    setSubmissionState((prev) => ({
      ...prev,
      isInitializing: true,
    }));

    try {
      const res = await createSubmission({
        accessToken: accessToken,
        intakeToken: intakeToken,
      }).unwrap();

      logToSentry("Intake submission initialized", "info", {
        intake_identifier: res.intake_identifier,
        context: "intake_form",
      });

      setSubmissionState((prev) => ({
        ...prev,
        submissionIdentifier: res.intake_identifier,
        isInitializing: false,
      }));

      return res.intake_identifier;
    } catch (error) {
      console.error("Failed to initialize submission:", error);

      logToSentry("Intake submission initialization failed", "error", {
        error: error instanceof Error ? error.message : "Unknown error",
        errorType: error instanceof Error ? error.name : typeof error,
        context: "intake_form",
      });

      KaboodleToaster.error("Failed to initialize form. Please try again.");

      setSubmissionState((prev) => ({
        ...prev,
        isInitializing: false,
      }));

      throw error;
    }
  }, [
    accessToken,
    intakeToken,
    createSubmission,
    submissionState.submissionIdentifier,
    submissionState.isInitializing,
  ]);

  // transform form data to match backend fields
  const transformFormData = (
    data: FormData,
    intakeIdentifier: string,
  ): IntakeSubmissionRequest => {
    if (!organizationSlug) {
      throw new Error("Organization slug is required for intake submission");
    }

    if (!intakeIdentifier) {
      throw new Error(
        "Submission identifier is required for intake submission",
      );
    }

    const documents = (data.documents ||
      []) as IntakeSubmissionRequest["documents"];

    return {
      organization_slug: organizationSlug,
      intake_identifier: intakeIdentifier,
      documents: documents,
      document_count: documents.length,
      adjuster: {
        name: data.adjusterName || "",
        email: data.adjusterEmail || null,
        street: data.adjusterStreet || "",
        apt: data.adjusterApt || "",
        city: data.adjusterCity || "",
        state: data.adjusterState || null,
        zip_code: data.adjusterZipCode || "",
        phone: data.adjusterPhone || "",
        adjuster_same_as_referring_party:
          data.adjuster_same_as_referring_party || false,
      },
      claimant: {
        first_name: data.claimantFirstName || "TBD",
        middle_initial: data.claimantMiddleInitial || "",
        last_name: data.claimantLastName || "TBD",
        gender: data.claimantGender || "O",
        ssn: data.claimantSSN || "",
        birth_date: data.claimantBirthDate || "01/01/2000",
        address: data.claimantAddress || "",
        city: data.claimantCity || "",
        state: data.claimantState || null,
        zip_code: data.claimantZipCode || "",
        medicare_id: data.claimantMedicareID || "",
        is_medicare_eligible: data.isClaimantMedicareEligible || false,
        reasonable_expectation: data.reasonableExpectation || false,
      },
      employer: {
        name: data.employerName || "",
        address: data.employerAddress || "",
        state: data.employerState || null,
        zipcode: data.employerZipCode || "",
      },
      insurer: {
        name: data.insurerName || "",
        address: data.insurerAddress || "",
        state: data.insurerState || null,
        zipcode: data.insurerZipCode || "",
      },
      tpa: {
        name: data.tpaName || "",
        address: data.tpaAddress || "",
        state: data.tpaState || null,
        zipcode: data.tpaZipCode || "",
      },
      dates_of_injury:
        data.dates_of_injury as IntakeSubmissionRequest["dates_of_injury"],
      is_rush: data.is_rush || false,
      is_24_hour_rush: data.is_24_hour_rush || false,
      is_3_day_rush: data.is_3_day_rush || false,
      is_update_to_prior_msa: data.is_update_to_prior_msa || false,
      due_date: data.due_date || "",
      is_represented: data.isRepresented || false,
      claimant_counsel: data.claimantCounsel || "",
      attorney_name: data.attorneyName || "",
      attorney_address: data.attorneyAddress || "",
      attorney_phone: data.attorneyPhone || "",
      services: (data.services || []).map((service) => ({
        type: service.value,
      })),
      insurance_types: (data.insuranceType || []).map((i) => ({
        type: i.value,
      })),
      settlement_reached: data.settlement_reached || false,
      estimated_settlement_amount: data.estimated_settlement_amount || "",
      estimated_medical_settlement_value:
        data.estimated_medical_settlement_value || "",
      reserve_settlement_amount: data.reserve_settlement_amount || "",
      tentative_settlement_minimum: data.tentative_settlement_minimum || "",
      tentative_settlement_maximum: data.tentative_settlement_maximum || "",
      preferred_structure_settlement_broker:
        data.preferred_structure_settlement_broker || "",
      awarded_ssd: data.awarded_ssd || false,
      applied_ssd: data.applied_ssd || false,
      referring_party_name: data.referring_party_name || "",
      ack_email_recipients: (data.additional_ack_email_recipients || [])
        .map((email) => email.trim())
        .filter(Boolean)
        .map((email) => ({ email })),
      // Liberty Mutual referral fields -- harmless to always include; the
      // backend only accepts them from Liberty Mutual/Helmsman submitters
      // and silently ignores them (not a validation error) for everyone
      // else.
      grs_or_legacy_rcg: data.grs_or_legacy_rcg || "",
      segmentation_type: data.segmentation_type || "",
      rcg_carrier: data.rcg_carrier || "",
      referral_type: data.referral_type || "",
      wcrc_case_id: data.wcrc_case_id || "",
      claim_notes: data.claim_notes || "",
    };
  };

  const submitForm = async (data: FormData) => {
    try {
      // ensure we have a submission identifier - initialize if needed (for submissions without documents)
      let currentSubmissionIdentifier = submissionState.submissionIdentifier;
      if (!currentSubmissionIdentifier) {
        currentSubmissionIdentifier = await initializeSubmission();
        if (!currentSubmissionIdentifier) {
          KaboodleToaster.error(
            "Failed to initialize submission. Please try again.",
          );
          return;
        }
      }

      setSubmissionState({
        isSubmitting: true,
        progress: 0,
        message: "Submitting form...",
        isComplete: false,
        showSuccessModal: false,
        submissionIdentifier: currentSubmissionIdentifier,
        isInitializing: false,
      });

      // check if dates_of_injury is empty and add placeholder if needed
      const processedData = { ...data };
      if (
        !processedData.dates_of_injury ||
        processedData.dates_of_injury.length === 0
      ) {
        processedData.dates_of_injury = [
          {
            date: "01/01/2000",
            claim_numbers: [{ number: "INTAKE-PLACEHOLDER-001" }],
            jurisdiction: "",
            wcmsa_zip_code: "",
            one_eleven_jurisdiction: "",
            accident_description: JSON.stringify({
              type: "doc",
              content: [
                {
                  type: "paragraph",
                  attrs: { textAlign: null },
                  content: [
                    {
                      type: "text",
                      text: `Referring party skipped and submitted the intake form`,
                      marks: [{ type: "italic" }],
                    },
                  ],
                },
              ],
            }),
            description_of_claimants_job: "",
            accepted_conditions: "",
            denied_conditions: "",
          },
        ];
      }

      const submissionData = transformFormData(
        processedData,
        currentSubmissionIdentifier,
      );

      await submitIntake({
        data: submissionData,
        accessToken: accessToken,
        intakeToken: intakeToken,
      }).unwrap();

      setSubmissionState((prev) => ({
        ...prev,
        progress: 100,
        message: "Submission complete",
        isSubmitting: false,
        isComplete: true,
        showSuccessModal: true,
      }));
    } catch (error) {
      setSubmissionState((prev) => ({
        ...prev,
        isSubmitting: false,
        progress: 0,
        message: "Preparing your submission...",
        isComplete: false,
        showSuccessModal: false,
      }));

      KaboodleToaster.error(
        `Failed to submit intake form: ${extractErrorMessage(error, "Please check the form and try again.")}`,
      );
    }
  };

  const handleCompleteAnother = async () => {
    // reset state - new submission will be created when first document is uploaded
    setSubmissionState({
      isSubmitting: false,
      progress: 0,
      message: "Preparing your submission...",
      isComplete: false,
      showSuccessModal: false,
      submissionIdentifier: null,
      isInitializing: false,
    });
  };

  return {
    isSubmitting: submissionState.isSubmitting,
    submissionProgress: submissionState.progress,
    submissionMessage: submissionState.message,
    isSubmissionComplete: submissionState.isComplete,
    showSuccessModal: submissionState.showSuccessModal,
    submissionIdentifier: submissionState.submissionIdentifier,
    isInitializing: submissionState.isInitializing,
    submitForm,
    handleCompleteAnother,
    initializeSubmission,
  };
};
